import {
  UrlParamKey,
  UrlQueryKey,
} from 'app/core/api/api-core/api-constants';
import {
  // RepairerInfo,
  UserRole,
} from 'app/core/api/user/api-user.types';
import { PageNotFoundError } from 'app/core/errors/error-variants';
import { contextActions } from 'app/core/redux/context-redux';
import { useAuthToken } from 'app/core/token/token-utils';
import IconLib from 'app/shared/components/icon/icon-lib';
import AppRoute from 'app/shared/frames/AppRoute';
import CommonAppRoutes from 'app/shared/page-parts/CommonAppRoutes';
import ChatList from 'app/shared/pages/Chat/ChatList';
import Tasks from 'app/shared/pages/Chat/Tasks';
import ResetPassword from 'app/shared/pages/ResetPassword';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import { LSKey } from 'app/tools/hooks/local-storage/local-storage.types';
import useLocalState from 'app/tools/hooks/local-storage/use-local-storage';
import { useQueryParams } from 'app/tools/hooks/misc-hooks';
import useSetupApp from 'app/tools/hooks/use-setup-app';
import imageSlide1 from 'assets/welcome-images/slide1.png';
import imageSlide2 from 'assets/welcome-images/slide2.png';
import imageSlide3 from 'assets/welcome-images/slide3.png';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useConsumerNotificationCount } from 'app/core/api/company/api-company';
import { Redirect, Switch, useHistory } from 'react-router-dom';
import ViewManual from 'app/shared/pages/ViewManual';
import {
  ADD_LABEL,
  NavActions,
} from '../shared/page-parts/BottomNav';
import ConfirmPassword from '../shared/pages/ConfirmPassword';
import PrivacyPolicy from '../shared/pages/PrivacyPolicy';
import TermsOfUse from '../shared/pages/TermsOfUse';
import { SlideInfo } from '../shared/pages/Welcome';
import ConsumerRoutePath from './ConsumerRoutePath';
import CreateNewPassword from './views/CreateNewPassword';
import Help from './views/Help';
import Home from './views/Home';
import ProductDetails from './views/inprogress/ProductDetail';
import AddAssignedProductDetails from './views/products/add-assigned-product-details/AddAssignedProductDetails';
import ManualProductEntry from './views/products/add-assigned-product-details/ManualProductEntry';
import ProductDatabaseLookupFailed from './views/products/ProductDatabaseLookupFailed';
import ProductDatabaseLookupResult from './views/products/ProductDatabaseLookupResult';
import ProductEnterBarcode from './views/products/ProductEnterBarcode';
import ProductScan from './views/products/ProductScan';
import ProductSearchCatalog from './views/products/ProductSearchCatalog';
import ViewProductDetails from './views/products/ProductDetails';
import ViewAssignedProduct from './views/products/view-assigned-product/ViewAssignedProduct';
import UserAddressEntry from './views/profile/UserAddressEntry';
import UserInfoEntry from './views/profile/UserInfoEntry';
import UserProfile from './views/profile/UserProfile';
import AddCard from './views/profile/wallet/AddCard';
import History from './views/profile/wallet/History';
import UserCardDetails from './views/profile/wallet/UserCardDetails';
import Wallet from './views/profile/wallet/Wallet';
import Refer from './views/Refer';
import SearchRepairer from './views/repairer-search/SearchRepairer';
import ReportFault from './views/report-fault/ReportFault';
import TermsAndConditions from './views/Welcome/TermsAndConditions';

const navActions: NavActions = [
  {
    label: 'Home',
    value: SharedRoutePath.HOME,
    icon: IconLib.HOME,
  },
  {
    label: 'Repairs',
    value: `${SharedRoutePath.CHAT_LIST}/all`,
    icon: IconLib.CHAT,
  },
  {
    label: ADD_LABEL,
    value: ConsumerRoutePath.PRODUCT_SCAN,
    icon: IconLib.SCAN,
  },
  {
    label: 'Help',
    value: ConsumerRoutePath.HELP,
    icon: IconLib.INFO,
  },
  {
    label: 'Profile',
    value: ConsumerRoutePath.PROFILE,
    icon: IconLib.USER,
  },
];

const welcomeSlides: SlideInfo[] = [
  {
    title: 'Welcome to iWarranty',
    subtitle: ['Good for your products,', 'great for our planet.'],
    imgSrc: imageSlide1,
  },
  {
    title: 'Skip the paperwork',
    subtitle: [
      'All your product information',
      'in the palm of your hand',
    ],
    imgSrc: imageSlide2,
  },
  {
    title: 'Instant peace of mind',
    subtitle: [
      'If something goes wrong, make a',
      'claim in a matter of seconds.',
    ],
    imgSrc: imageSlide3,
  },
];

export default function ConsumerApp(): JSX.Element {
  const { resource: notificationCount } =
    useConsumerNotificationCount({
      fresh: true,
    });
  const dispatch = useDispatch();
  useEffect(() => {
    const navs: NavActions = [
      {
        label: 'Home',
        value: SharedRoutePath.HOME,
        icon: IconLib.HOME,
      },
      {
        label: 'Repairs',
        value: `${SharedRoutePath.CHAT_LIST}/all`,
        icon: IconLib.CHAT,
        data: notificationCount?.count,
      },
      {
        label: ADD_LABEL,
        value: ConsumerRoutePath.PRODUCT_SCAN,
        icon: IconLib.SCAN,
      },
      {
        label: 'Help',
        value: ConsumerRoutePath.HELP,
        icon: IconLib.INFO,
      },
      {
        label: 'Profile',
        value: ConsumerRoutePath.PROFILE,
        icon: IconLib.USER,
      },
    ];
    dispatch(contextActions.navActions.set(navs));
  }, [dispatch, notificationCount]);
  const { showWelcome } = useSetupApp(navActions);
  const authToken = useAuthToken();
  const params = useQueryParams();
  const history = useHistory();

  const refCode = params.get(UrlQueryKey.REF_CODE) || '';
  const [, setRefStore] = useLocalState(LSKey.REF_CODE, refCode);

  useEffect(() => {
    if (refCode && !authToken) {
      setRefStore(refCode.replace('/', '')); // removing any slashes
      if (!showWelcome) history.replace(SharedRoutePath.SIGN_UP);
    } else if (authToken) {
      setRefStore('');
    }
  }, [authToken, history, refCode, setRefStore, showWelcome]);

  if (showWelcome) return <Redirect to={SharedRoutePath.WELCOME} />;

  return (
    <Switch>
      {/* ------- PUBLIC ROUTES ------- */}
      {CommonAppRoutes(welcomeSlides)}
      <AppRoute
        publicRoute
        path={SharedRoutePath.RESET_PASSWORD}
        display={<ResetPassword />}
        fullScreen
        soloContent
      />
      <AppRoute
        publicRoute
        path={SharedRoutePath.CONFIRM_PASSWORD}
        display={<ConfirmPassword />}
        fullScreen
        soloContent
      />
      <AppRoute
        publicRoute
        // path={SharedRoutePath.VIEW_MANUAL}
        path={`${SharedRoutePath.VIEW_MANUAL}/:${UrlParamKey.ID}`}
        display={<ViewManual />}
        backButton
      />
      <AppRoute
        title="Data & Privacy Policy"
        publicRoute
        path={SharedRoutePath.PRIVACY_POLICY}
        display={<PrivacyPolicy />}
      />
      <AppRoute
        title="Terms of Use"
        publicRoute
        path={SharedRoutePath.TERMS_Of_USE}
        display={<TermsOfUse />}
      />

      {/* --------- HOME ROUTE -------- */}
      <AppRoute
        title="Home"
        path={SharedRoutePath.HOME}
        exact
        display={<Home />}
        cardBgColor
      />

      {/* ---- PRODUCTS SUB-ROUTES ---- */}
      <AppRoute
        title="Product Details"
        path={`${ConsumerRoutePath.VIEW_PRODUCT_DETAILS}/:${UrlParamKey.ID}`}
        display={<ViewProductDetails />}
        backButton
      />
      <AppRoute
        title="Product Details"
        path={ConsumerRoutePath.VIEW_PRODUCT_DETAILS}
        display={<ViewProductDetails />}
        backButton
      />

      <AppRoute
        title="Product Details"
        path={`${ConsumerRoutePath.VIEW_ASSIGNED_PRODUCT}/:${UrlParamKey.ID}`}
        display={<ViewAssignedProduct />}
        backButton
      />

      <AppRoute
        path={`${ConsumerRoutePath.REPAIR_VIEW_PRODUCT}/:${UrlParamKey.ID}`}
        display={<ProductDetails />}
        backButton
        edgeToEdge
      />
      <AppRoute
        path={ConsumerRoutePath.PRODUCT_SCAN}
        display={<ProductScan />}
        fullScreen
        edgeToEdge
      />
      <AppRoute
        title="Enter barcode"
        path={ConsumerRoutePath.PRODUCT_ENTER_BARCODE}
        display={<ProductEnterBarcode />}
        backButton
      />
      <AppRoute
        title="Add your product"
        path={ConsumerRoutePath.PRODUCT_SEARCH_CATALOG}
        display={<ProductSearchCatalog />}
        backButton
      />
      <AppRoute
        path={ConsumerRoutePath.PRODUCT_DATABASE_LOOKUP_RESULT}
        display={<ProductDatabaseLookupResult />}
        backButton
      />
      <AppRoute
        path={ConsumerRoutePath.PRODUCT_DATABASE_LOOKUP_FAILED}
        display={<ProductDatabaseLookupFailed />}
        backButton
        soloContent
      />
      <AppRoute
        title="Create Product"
        path={ConsumerRoutePath.MANUAL_PRODUCT_ENTRY}
        display={<ManualProductEntry />}
        backButton
      />
      <AppRoute
        path={ConsumerRoutePath.ADD_ASSIGNED_PRODUCT_DETAILS}
        display={<AddAssignedProductDetails />}
        backButton
      />

      {/* ---- REPAIRS SUB-ROUTES ---- */}
      <AppRoute
        title="Repair Item"
        path={`${ConsumerRoutePath.PRODUCT_REPORT_FAULT}/:${UrlParamKey.ID}`}
        display={<ReportFault />}
        backButton
      />
      <AppRoute
        title="Search Repairer"
        path={`${ConsumerRoutePath.PRODUCT_SEARCH_REPAIRER}/:${UrlParamKey.ID}`}
        display={<SearchRepairer />}
        edgeToEdge
        backButton
      />
      <AppRoute
        title="Chats"
        path={`${SharedRoutePath.CHAT_LIST}/:${UrlParamKey.ID}`}
        display={<ChatList userRole={UserRole.CONSUMER} />}
      />
      <AppRoute
        title="Chats"
        path={SharedRoutePath.CHAT_LIST}
        display={<ChatList userRole={UserRole.CONSUMER} />}
      />
      <AppRoute
        path={`${SharedRoutePath.CHAT}/:${UrlParamKey.ID}`}
        display={<Tasks userRole={UserRole.CONSUMER} />}
        backButton
        fullScreen="showHeader"
        edgeToEdge
      />

      {/* ---- OTHER PRIVATE ROUTES ---- */}
      <AppRoute
        title="Profile"
        path={ConsumerRoutePath.PROFILE}
        exact
        display={<UserProfile />}
      />
      <AppRoute
        title="Edit Profile"
        backButton
        path={ConsumerRoutePath.USER_INFO}
        display={<UserInfoEntry />}
      />
      <AppRoute
        title="Edit User Address"
        backButton
        path={ConsumerRoutePath.USER_ADDRESS}
        display={<UserAddressEntry />}
      />
      <AppRoute
        title="Help"
        path={ConsumerRoutePath.HELP}
        display={<Help />}
      />
      <AppRoute
        title="Refer a Friend"
        path={ConsumerRoutePath.REFER}
        backButton
        display={<Refer />}
      />
      <AppRoute
        title="Change password"
        path={SharedRoutePath.CHANGE_PASSWORD}
        display={<CreateNewPassword />}
        backButton
        soloContent
      />
      <AppRoute
        path={SharedRoutePath.FORCE_NEW_PASSWORD}
        display={<CreateNewPassword forceReset />}
        fullScreen
        soloContent
      />
      <AppRoute
        path={ConsumerRoutePath.WALLET}
        display={<Wallet />}
        backButton
      />
      <AppRoute
        path={ConsumerRoutePath.ADD_CARD}
        display={<AddCard />}
        backButton
      />
      <AppRoute
        path={ConsumerRoutePath.USER_CARD_DETAILS}
        display={<UserCardDetails />}
        backButton
      />
      <AppRoute
        title="History"
        path={ConsumerRoutePath.HISTORY}
        display={<History />}
        backButton
      />
      <AppRoute
        title="Competition Terms"
        path={ConsumerRoutePath.COMPETITION_TERMS}
        display={<TermsAndConditions />}
        backButton
      />

      {/* -------- NOT-FOUND ROUTE ------ */}
      <AppRoute
        path="*"
        display={() => {
          throw new PageNotFoundError();
        }}
        publicRoute
      />
    </Switch>
  );
}
